import { Component, EventEmitter, OnDestroy, OnInit, Output, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '@app/core/services/Auth/auth-service';
import { ValidationConstants } from '@app/constants/validation.constants';
import { Spinner } from '../spinner/spinner';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-change-password-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    Spinner
  ],
  templateUrl: './change-password-form.html',
  styleUrls: ['./change-password-form.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChangePasswordFormComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();

  changePasswordForm: FormGroup;
  hideCurrentPassword = true;
  hideNewPassword = true;
  hideConfirmPassword = true;
  loading = signal(false);
  success = signal(false);
  error = signal<string | null>(null);

  constructor(private fb: FormBuilder, private authservice: AuthService,private toastr: ToastrService
  ) {
    this.changePasswordForm = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(8), Validators.pattern(ValidationConstants.PASSWORD_PATTERN)]],
      confirmPassword: ['', Validators.required]
    }, { validator: this.passwordMatchValidator });
    this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(() => {
      const confirmControl = this.changePasswordForm.get('confirmPassword');
      if (confirmControl && confirmControl.value) {
        confirmControl.markAsTouched();
      }
    });
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { mismatch: true };
  }

  onSubmit() {
    if (this.changePasswordForm.valid) {
      console.log('Form Submitted', this.changePasswordForm.value);
       this.loading.set(true);
    this.success.set(false);
    this.error.set(null);

    const { oldPassword, newPassword } = this.changePasswordForm.value;
    this.authservice.changePassword({ oldPassword, newPassword }).subscribe({
      next: (response:any) => {
        this.toastr.success(response.message||'Password changed successfully');
        this.loading.set(false);
        this.success.set(true);
        this.changePasswordForm.reset();
        this.close.emit();
      },
      error: (err) => {
        this.loading.set(false);
        this.toastr.error(err?.error?.message || 'Failed to change password');
      }
    });

    }
  }

  onCancel() {
    this.close.emit();
  }
   getErrorMessage(controlName: string, formGroup: FormGroup): string {
      const control = formGroup.get(controlName);
      if (controlName === 'email') {
        return ValidationConstants.getEmailOrPhoneErrorMessage(control!);
      }
      if (controlName === 'confirmPassword' && formGroup.errors?.['mismatch'] && control?.touched) {
        return 'Passwords do not match';
      }

      if (!control?.errors || !control.touched) return '';

      if (controlName === 'oldPassword') {
        if (control.errors['required']) return 'Current password is required';
      }

      if (controlName === 'newPassword') {
        if (control.errors['required']) return 'Password is required';
        if (control.errors['pattern']) {
          return '`Password must be at least 8 characters with at least 1 uppercase, 1 number, and 1 special character`';
        }
        if (control.errors['minlength']) return 'Password must be at least 8 characters';
      }

      if (controlName === 'confirmPassword' && control.errors['required']) {
        return 'Please confirm your password';
      }

      return '';
    }

  // Getter methods for form controls
  get oldPasswordControl(): FormControl {
    return this.changePasswordForm.get('oldPassword') as FormControl;
  }

  get newPasswordControl(): FormControl {
    return this.changePasswordForm.get('newPassword') as FormControl;
  }

  get confirmPasswordControl(): FormControl {
    return this.changePasswordForm.get('confirmPassword') as FormControl;
  }

  ngOnInit() {
    document.body.classList.add('modal-open');
  }
    ngOnDestroy() {
      document.body.classList.remove('modal-open');
    }
}
